import React from 'react';
import { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { TooltipIcon, TooltipWrapper } from 'components/Tooltip';
import { Text } from 'components/Typography';
import { theme } from 'design';
import { IPaymentTotals, ITipCalculatorParticipant } from 'types';
import { centsToDollarsDisplay, formatDateForUILongFormat } from 'utils';
import Sidebar, { ISidebarNotification } from './components/Sidebar';
import { RowItem as BaseRowItem, Content } from './components/styles';

interface IPaymentReviewSidebarProps {
  dataCy?: string;
  businessDate: Dayjs;
  continueDisabled?: boolean;
  showHoldbacks?: boolean;
  showCash?: boolean;
  isTipCalculator?: boolean;
  totals?: IPaymentTotals;
  participants?: ITipCalculatorParticipant[];
  notifications?: ISidebarNotification[];
  onBack: () => void;
  onContinue?: () => void;
}

const RowItem = styled(BaseRowItem)`
  &.gross {
    p {
      color: ${theme.color.neutral[900]};
    }
  }
`;

const PaymentReviewSidebar = ({
  businessDate,
  totals,
  participants,
  notifications,
  isTipCalculator = false,
  showHoldbacks = true,
  showCash = true,
  onBack,
  onContinue,
  continueDisabled = false,
  dataCy = 'payment-review-sidebar',
}: IPaymentReviewSidebarProps) => (
  <Sidebar
    title="Payment Summary"
    leftActionButton={{ label: 'Back', appearance: 'white', onClick: onBack }}
    rightActionButton={onContinue ? { label: 'Continue', appearance: 'primary', disabled: continueDisabled || notifications?.some((notification) => notification.preventSubmit), onClick: onContinue } : undefined}
    notifications={notifications}
    dataCy={dataCy}
  >
    <Content>
      <RowItem data-cy={`${dataCy}-business-date`} className="business-date">
        <Text size="small" data-cy={`${dataCy}-business-date-label`}>
          Business Date:
        </Text>
        <Text size="small" data-cy={`${dataCy}-business-date-value`}>
          {formatDateForUILongFormat(businessDate)}
        </Text>
      </RowItem>
      <RowItem data-cy={`${dataCy}-gross`} className="gross">
        <Text size="small" fontWeight="medium" data-cy={`${dataCy}-gross-label`}>
          {isTipCalculator ? 'Calculated Amount (Gross)' : 'Amount Entered (Gross)'}
        </Text>
        <Text size="small" fontWeight="medium" data-cy={`${dataCy}-gross-value`}>
          {centsToDollarsDisplay(totals?.gross ?? 0)}
        </Text>
      </RowItem>
      {showCash && (
        <RowItem data-cy={`${dataCy}-cash`}>
          <Text size="small" data-cy={`${dataCy}-cash-label`}>
            Cash
          </Text>
          <Text size="small" data-cy={`${dataCy}-cash-value`}>
            {centsToDollarsDisplay(totals?.cash ?? 0)}
          </Text>
        </RowItem>
      )}

      <RowItem data-cy={`${dataCy}-payroll`}>
        <Text size="small" data-cy={`${dataCy}-payroll-label`}>
          Payroll
        </Text>
        <Text size="small" data-cy={`${dataCy}-payroll-value`}>
          {centsToDollarsDisplay(totals?.payroll ?? 0)}
        </Text>
      </RowItem>

      {participants?.map((participant) => (
        <RowItem key={`participant_${participant.name}`} data-cy={`${dataCy}-participant`}>
          <Text size="small" data-cy={`${dataCy}-participant-label`}>
            {participant.name}
          </Text>
          <Text size="small" data-cy={`${dataCy}-participant-value`}>
            {centsToDollarsDisplay(participant.net)}
          </Text>
        </RowItem>
      ))}
      <RowItem data-cy={`${dataCy}-instant`}>
        <Text size="small" data-cy={`${dataCy}-instant-label`}>
          Instant
        </Text>
        <Text size="small" data-cy={`${dataCy}-instant-value`}>
          {centsToDollarsDisplay(totals?.instant ?? 0)}
        </Text>
      </RowItem>
      {showHoldbacks && (
        <RowItem data-cy={`${dataCy}-holdbacks`}>
          <TooltipWrapper>
            <Text size="small" data-cy={`${dataCy}-holdbacks-label`}>
              Holdbacks
            </Text>
            <TooltipIcon id="holdback">Amount withheld to cover expenses</TooltipIcon>
          </TooltipWrapper>
          <Text size="small" data-cy={`${dataCy}-holdbacks-value`}>
            -{centsToDollarsDisplay(totals?.holdbacks ?? 0)}
          </Text>
        </RowItem>
      )}
      <RowItem data-cy={`${dataCy}-pay-now`} className="gross">
        <Text size="small" fontWeight="medium" data-cy={`${dataCy}-pay-now-label`}>
          Pay Now
        </Text>
        <Text size="small" fontWeight="medium" data-cy={`${dataCy}-pay-now-value`}>
          {centsToDollarsDisplay(totals?.payNow ?? 0)}
        </Text>
      </RowItem>
    </Content>
  </Sidebar>
);

export default PaymentReviewSidebar;
